import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Input,
  input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core'
import { ControlContainer, UntypedFormControl, ValidationErrors } from '@angular/forms'
import { ErrorStateMatcher } from '@angular/material/core'
import { MatFormField, MatFormFieldAppearance } from '@angular/material/form-field'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { debounceTime } from 'rxjs'
import { IInputErrorMessages, ISelectItem } from './input.interface'
import { NormalErrorMatcher } from '@lla-platform/core/core-util'
import { globalErrorMessages } from './input.constant'
import { YesterdayString } from '@lla-platform/core/core-data-access'

@UntilDestroy()
@Component({
  selector: 'lla-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class InputComponent implements OnInit, OnChanges, AfterViewChecked, AfterViewInit {
  @ViewChildren(MatFormField) formFields: QueryList<MatFormField>
  @ViewChild('textareaElement') textareaElement: ElementRef<HTMLTextAreaElement>

  @Input() normalInputId: string
  @Input() controlName: string
  @Input() label: string
  @Input() prefix: string
  @Input() size: 'smaller' | 'small' | 'default' = 'default'
  @Input() appearance: MatFormFieldAppearance = 'outline'
  @Input() loginInput = false
  @Input() whiteMode = false
  @Input() mask: string
  @Input() maskSuffix = ''
  useCurrencyMask = input(false)
  currencyMaskOption = input<{
    align?: 'left' | 'right'
    prefix?: string
    suffix?: string
    precision?: number
    allowNegative?: boolean
  }>({ align: 'left', prefix: '$' })
  @Input() debounceDuration = 0
  @Input() type:
    'text' | 'email' | 'password' | 'number' | 'select' | 'date' | 'google-places' | 'textarea' = 'text'
  @Input() showErrorMessage = true
  @Input() errorMessages: IInputErrorMessages[] = []
  @Input() customErrorMatcher: ErrorStateMatcher
  @Input() customError: {
    show: boolean
    message: string
  }
  @Input() autoFocus = false
  @Input() loading = false
  @Input() disabled = false
  @Input() readonly = false
  @Input() items: ISelectItem[] = []
  @Input() icon: string
  @Input() iconColor = '#808080'
  @Input() iconClass = ''
  @Input() iconSize = '16px'
  @Input() minTextAreaRows = 7
  @Input() maxTextAreaRows = 10
  @Input() extraSelectPanelClass = ''
  @Input() maxDate?: Date | string = YesterdayString()
  @Input() minDate?: Date | string
  @Input() placeholder = ''
  @Input() clearable = false
  @Input() clearIconColor = '#808080'
  @Input() clearIconSize = '12px'
  @Input() rightAlign = false
  @Input() inputmode?: string
  @Input() maxLength?: number

  @Output() valueChanged = new EventEmitter()
  @Output() googlePlaceAddressChanged = new EventEmitter()
  @Output() iconClicked = new EventEmitter()
  @Output() inputFocusOut = new EventEmitter()
  @Output() inputFocus = new EventEmitter()

  formControl: UntypedFormControl
  minimizeLabel = false
  allErrorMessages: IInputErrorMessages[] = []
  showPassword = false
  inputValue: string | null
  isFocused = false

  errorMatcher = new NormalErrorMatcher()

  // Computed currency mask properties for ngx-mask
  currencyMaskPattern = computed(() => `separator.${this.currencyMaskOption()?.precision ?? 2}`)
  currencyMaskPrefix = computed(() => this.currencyMaskOption()?.prefix ?? '$ ')
  currencyMaskSuffix = computed(() => this.currencyMaskOption()?.suffix ?? '')
  currencyMaskAlign = computed(() => this.currencyMaskOption()?.align ?? 'left')
  currencyMaskAllowNegative = computed(() => this.currencyMaskOption()?.allowNegative !== false)

  constructor(
    private controlContainer: ControlContainer,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.formControl = this.controlContainer.control?.get(this.controlName) as UntypedFormControl

    this.allErrorMessages = [...this.errorMessages, ...globalErrorMessages]
    this.formControl?.valueChanges
      .pipe(untilDestroyed(this), debounceTime(this.debounceDuration))
      .subscribe((value) => {
        this.valueChanged.emit(value)
      })

    this.changeDisableState()
  }

  ngAfterViewInit(): void {
    if (this.type === 'google-places') {
      const input = document.getElementById('google-places-input') as HTMLInputElement
      if (input) {
        const autocomplete = new google.maps.places.Autocomplete(input, {
          types: ['establishment'],
          componentRestrictions: { country: 'US' }
        })
        google.maps.event.addListener(autocomplete, 'place_changed', () => {
          this.googlePlaceAddressChanged.emit(autocomplete.getPlace())
        })
      }
    }
  }

  ngOnChanges() {
    this.changeDisableState()
  }

  ngAfterViewChecked(): void {
    // Fix mat select error when the option list is empty
    if (this.type === 'select') {
      this.changeDetectorRef.detectChanges()
    }
  }

  handleErrorMessage() {
    if (this.customError?.show && this.customError?.message) {
      return this.customError.message
    }
    const allErrors: ValidationErrors = this.formControl?.errors ?? {}
    return this.allErrorMessages.find((el) => allErrors[el.type])?.message ?? ''
  }

  isFormControlInvalid() {
    return (
      (this.customError?.show || this.formControl?.invalid) &&
      (this.formControl?.dirty || this.formControl?.touched)
    )
  }

  changeDisableState() {
    if (this.formControl) {
      if (this.formControl.enabled && (this.loading || this.disabled)) {
        this.formControl?.disable()
      } else if (this.formControl.disabled) {
        this.formControl?.enable()
      }
    }
  }

  onTextareaInput() {
    if (this.textareaElement?.nativeElement) {
      const textarea = this.textareaElement.nativeElement
      // Scroll to the bottom to keep the cursor visible
      textarea.scrollTop = textarea.scrollHeight
    }
  }
}
